import { ComponentRef, Injectable } from '@angular/core';
import { DetachedRouteHandle } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class TabReuseCacheService {
  private readonly handles = new Map<string, DetachedRouteHandle>();
  private readonly openTabUrls = new Set<string>();

  hasCachedHandle(key: string): boolean {
    return this.handles.has(key);
  }

  hasOpenTab(url: string): boolean {
    return this.openTabUrls.has(this.normalizeUrl(url));
  }

  syncOpenTabUrls(urls: readonly string[]): void {
    this.openTabUrls.clear();
    for (const url of urls) {
      const normalizedUrl = this.normalizeUrl(url);
      if (normalizedUrl) {
        this.openTabUrls.add(normalizedUrl);
      }
    }
  }

  get(key: string): DetachedRouteHandle | null {
    return this.handles.get(key) ?? null;
  }

  set(key: string, handle: DetachedRouteHandle): void {
    const existing = this.handles.get(key);
    if (existing && existing !== handle) {
      this.destroyHandle(existing);
    }

    this.handles.set(key, handle);
  }

  delete(key: string): void {
    const handle = this.handles.get(key);
    if (handle) {
      this.destroyHandle(handle);
    }

    this.handles.delete(key);
  }

  destroyDetachedHandle(handle: DetachedRouteHandle): void {
    this.destroyHandle(handle);
  }

  clear(): void {
    for (const handle of this.handles.values()) {
      this.destroyHandle(handle);
    }

    this.handles.clear();
    this.openTabUrls.clear();
  }

  private destroyHandle(handle: DetachedRouteHandle): void {
    const componentRef = (handle as { componentRef?: ComponentRef<unknown> | null }).componentRef;
    componentRef?.destroy();
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return '';
    }

    const path = url.split(/[?#]/)[0] ?? url;
    return path.startsWith('/') ? path : `/${path}`;
  }
}
